import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'
import { useEffect, useRef } from 'react'

import { saleStatusCheckRequested } from './commands'
import { PROGRESS_POLL_MS, PROGRESS_SAFETY_POLL_MS } from '@krp/sale-ui'
import type { SaleOperationVm } from './types'

/**
 * Ход операции продажи, пока окно открыто и видно: чтение нашего журнала раз в секунду.
 * Кабинет опрашивает серверный наблюдатель независимо от браузера. Автоматическая сверка
 * из браузера гонялась бы с ним по версии операции; ручная сверка остаётся отдельным действием.
 */
export function useSaleProgress(
  resourceId: string,
  seat: { registerKey: string; deviceId: number } | null,
  operation: SaleOperationVm | null,
  dispatch: Dispatch<UnknownAction>,
  /**
   * Окну нужна правда сервера, а не своя команда: ответа на команду оплаты нет (пока служба не назовёт операцию) или оплата прошла и
   * чек отправляет служба (RP-FB35 K1). Продажу перечитывает само окно — кассиру кнопок не нужно, чек окно не отправляет.
   */
  rereadSale = false,
  /**
   * Деньги получены, а чек не пробит («Пробейте чек»): служба может отправить чек позже (вебхук, сторож, принятый «Повторить проведение»
   * по расписанию). Окно перечитывает продажу реже, раз в {@link PROGRESS_SAFETY_POLL_MS}, чтобы не застыть на устаревшем «чек не пробит».
   */
  slowReread = false,
): void {
  const current = useRef(operation)
  useEffect(() => {
    current.current = operation
  }, [operation])
  const registerKey = seat?.registerKey ?? null
  const deviceId = seat?.deviceId ?? null
  const polling = registerKey !== null && operation?.polling === true

  // Это чтение нашего журнала, а не повтор команды кассе. RTK Query объединяет запросы одного ключа, пока ответ не получен.
  const rereadEvery = rereadSale || polling ? PROGRESS_POLL_MS : slowReread ? PROGRESS_SAFETY_POLL_MS : null
  useEffect(() => {
    if (rereadEvery === null || registerKey === null || deviceId === null) return undefined
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      dispatch(saleStatusCheckRequested({ resourceId, registerKey, deviceId, operationId: current.current?.operationId ?? null }))
    }, rereadEvery)
    return () => clearInterval(timer)
  }, [rereadEvery, deviceId, dispatch, resourceId, registerKey])
}
